import { apiClient } from '@/lib/api';
import { conCambioPropio } from '../lib/cambio-propio';
import type { ClaveDeSalida } from '@/features/pricing-rules/types/pricing-rules.types';
import type {
  DetalleDeLaPropuesta,
  FacetasDeLaBandeja,
  FiltroDeMotivo,
  HojaDeVuelta,
  MotivoAlDescartar,
  MotivoPendiente,
  OrigenDeLaPropuesta,
  PriceProposalsPage,
  PropuestaDelHistorial,
  PropuestasParecidas,
  ResultadoDeParecidas,
  ResultadoDeVolver,
  ProposalCounts,
  ProposalStatus,
  ResultadoDeLote,
  ResultadoPorPropuesta,
  ResumenHoy,
} from '../types/price-proposals.types';
import { avisarPublicacion } from './publicacion-en-tienda';

/**
 * Saneamiento del 26-sep (T7): el precio que la persona vio sólo se manda si es
 * finito y mayor que 0. El backend rechaza con 422 cualquier otro — en el lote,
 * uno solo voltearía el pedido entero —; sin él, revalida y dice `SALIDA_INVALIDA`
 * para esa propuesta sola.
 */
const esPrecioVistoValido = (n: number | null | undefined): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0;

/** Los filtros de la lista y de sus facetas (lote B1: también categoría y marca). */
interface FiltrosDeLaLista {
  status?: ProposalStatus;
  search?: string;
  strategyApplicationId?: string;
  /** 4B-datos T4: sólo las sin precio posible. */
  sinPrecioPosible?: boolean;
  /** 4B-datos T4: por qué quedó esperando. */
  motivo?: MotivoPendiente;
  /** 4B-datos T4 (S6 «Ver cuáles»): sólo las que una regla limitó. */
  limitadaPorRegla?: boolean;
  /** 4B-datos T4: creadas en los últimos N días (1–90). */
  dias?: number;
  /** Lote B1: de elección múltiple; '' = sin categoría / sin marca. */
  categorias?: string[];
  marcas?: string[];
  /** Lote B1 (D2): Descartadas por motivo. */
  motivoDescarte?: FiltroDeMotivo;
  /** Lote B2 (punto 7): cuándo se decidió, instantes ISO (00:00 y 23:59 de Bolivia). */
  decididaDesde?: string;
  decididaHasta?: string;
  /** Spec 275 (R2, D-R2-9): cuándo se calculó, del calendario de Por decidir. */
  calculadaDesde?: string;
  calculadaHasta?: string;
  /** Lote C1 (punto 8): sólo las de un origen. */
  origen?: OrigenDeLaPropuesta;
  /** Lote C2b: sólo las de un producto (para llegar a su fila abierta). */
  productId?: string;
}

/** El cuerpo del descarte: sólo lo que se eligió (sin nada, el backend guarda «sin motivo»). */
function cuerpoDelMotivo(m?: MotivoAlDescartar): Partial<MotivoAlDescartar> | undefined {
  if (!m) return undefined;
  const cuerpo: Partial<MotivoAlDescartar> = {};
  if (m.motivo) cuerpo.motivo = m.motivo;
  if (m.nota?.trim()) cuerpo.nota = m.nota.trim();
  return Object.keys(cuerpo).length > 0 ? cuerpo : undefined;
}

/** El mismo texto de consulta para la lista y las facetas: tienen que filtrar igual. */
function consultaDe(params: FiltrosDeLaLista): URLSearchParams {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.search) q.set('search', params.search);
  if (params.strategyApplicationId) q.set('strategyApplicationId', params.strategyApplicationId);
  if (params.sinPrecioPosible) q.set('sinPrecioPosible', 'true');
  if (params.motivo) q.set('motivo', params.motivo);
  if (params.limitadaPorRegla) q.set('limitadaPorRegla', 'true');
  if (params.dias) q.set('dias', String(params.dias));
  if (params.motivoDescarte) q.set('motivoDescarte', params.motivoDescarte);
  if (params.decididaDesde) q.set('decididaDesde', params.decididaDesde);
  if (params.decididaHasta) q.set('decididaHasta', params.decididaHasta);
  if (params.calculadaDesde) q.set('calculadaDesde', params.calculadaDesde);
  if (params.calculadaHasta) q.set('calculadaHasta', params.calculadaHasta);
  if (params.origen) q.set('origen', params.origen);
  if (params.productId) q.set('productId', params.productId);
  // Repetido (`categoria=a&categoria=b`), no `categoria[]`: así lo lee el backend.
  for (const c of params.categorias ?? []) q.append('categoria', c);
  for (const m of params.marcas ?? []) q.append('marca', m);
  return q;
}

/** La bandeja de propuestas — Etapa 4 (plan 167). */
export const priceProposalsApi = {
  listar: async (
    params: FiltrosDeLaLista & { page?: number; pageSize?: number }
  ): Promise<PriceProposalsPage> => {
    const q = consultaDe(params);
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));

    return apiClient.get<PriceProposalsPage>(`/api/price-proposals?${q.toString()}`);
  },

  /** Lote B1: lo de la izquierda de la fila abierta (estrategia, desde cuándo, último cambio, reglas). */
  detalle: async (id: string): Promise<DetalleDeLaPropuesta> =>
    apiClient.get<DetalleDeLaPropuesta>(`/api/price-proposals/${encodeURIComponent(id)}/detalle`),

  /** Lote B1: los valores de los filtros de categoría y marca, con cuántas tiene cada uno. */
  facetas: async (params: FiltrosDeLaLista): Promise<FacetasDeLaBandeja> =>
    apiClient.get<FacetasDeLaBandeja>(
      `/api/price-proposals/facetas?${consultaDe(params).toString()}`
    ),

  contar: async (): Promise<ProposalCounts> =>
    apiClient.get<ProposalCounts>('/api/price-proposals/resumen'),

  /** Spec 275 (R3, D-R3-2): la firma de la bandeja para «Hay cambios nuevos». */
  version: (): Promise<{ firma: string }> => apiClient.get('/api/price-proposals/version'),

  /** Qué hay que decidir hoy (plan 173). */
  hoy: async (): Promise<ResumenHoy> => apiClient.get<ResumenHoy>('/api/price-proposals/hoy'),

  /** Plan 3, T3b (P3-D6): lo que las estrategias le hicieron a un producto. */
  delProducto: async (productId: string): Promise<PropuestaDelHistorial[]> =>
    (
      await apiClient.get<{ items: PropuestaDelHistorial[] }>(
        `/api/price-proposals/producto/${productId}`
      )
    ).items,

  /**
   * Plan 4A: en una sin precio posible se elige la salida por su clave. Una
   * clave que no es de la propuesta, o un precio imposible: 422 (se lee con
   * `mensajeDeError`). `precioVisto` (saneamiento, T7): el precio que la fila o
   * la salida elegida mostraban; si la propuesta cambió, `PROPUESTA_CAMBIO`.
   * `aceptaPerdida` (lote A, A1): la persona confirmó que queda debajo del
   * costo; sin él, una así vuelve `DEBAJO_DEL_COSTO` y no se aplica.
   */
  aprobar: async (
    id: string,
    salida?: ClaveDeSalida,
    precioVisto?: number,
    aceptaPerdida?: boolean
  ): Promise<ResultadoPorPropuesta> => {
    const cuerpo: { salida?: ClaveDeSalida; precioVisto?: number; aceptaPerdida?: true } = {};
    if (salida) cuerpo.salida = salida;
    if (esPrecioVistoValido(precioVisto)) cuerpo.precioVisto = precioVisto;
    if (aceptaPerdida) cuerpo.aceptaPerdida = true;
    // Spec 275 (R3): es una acción propia, la franja «Hay cambios nuevos» no avisa.
    const r = await conCambioPropio(
      apiClient.post<ResultadoPorPropuesta>(
        `/api/price-proposals/${id}/approve`,
        Object.keys(cuerpo).length > 0 ? cuerpo : undefined
      )
    );
    // «Publicar al aprobar» (25-oct): si llegó a la tienda online, se dice.
    avisarPublicacion(r);
    return r;
  },

  /** Las pendientes sin precio posible con las mismas reglas que no se pueden cumplir juntas (decisión 6). */
  similares: async (id: string): Promise<PropuestasParecidas> =>
    apiClient.get<PropuestasParecidas>(`/api/price-proposals/${id}/similares`),

  /**
   * La misma clave a todas las parecidas, cada una con su propio precio. Tope 200.
   * No acepta `precioVisto`: cada una aplica el precio que tenga en ese momento.
   */
  aprobarSimilares: async (id: string, salida: ClaveDeSalida): Promise<ResultadoDeParecidas> => {
    const r = await conCambioPropio(
      apiClient.post<ResultadoDeParecidas>(
        `/api/price-proposals/${id}/approve-similares`,
        { salida },
        { timeout: 180000 }
      )
    );
    avisarPublicacion(r);
    return r;
  },

  /** Lote B1 (D2): con el motivo, opcional. */
  /** Spec 275 (R3, D-R3-4): `precioVisto`, el precio propuesto que mostraba la fila. */
  descartar: async (
    id: string,
    motivo?: MotivoAlDescartar,
    precioVisto?: number
  ): Promise<ResultadoPorPropuesta> => {
    const cuerpo = {
      ...cuerpoDelMotivo(motivo),
      ...(esPrecioVistoValido(precioVisto) ? { precioVisto } : {}),
    };
    // Sin nada, sin cuerpo (D2).
    return conCambioPropio(
      apiClient.post<ResultadoPorPropuesta>(
        `/api/price-proposals/${id}/discard`,
        Object.keys(cuerpo).length > 0 ? cuerpo : undefined
      )
    );
  },

  /** Deshace un descarte (28-sep): la propuesta vuelve a Por decidir, sin tocar el precio. */
  volverAPorDecidir: async (id: string): Promise<ResultadoDeVolver> =>
    conCambioPropio(
      apiClient.post<ResultadoDeVolver>(`/api/price-proposals/${id}/volver-a-por-decidir`)
    ),

  /** `preciosVistos` (saneamiento, T7): el precio que mostraba cada fila. */
  aprobarLote: async (
    ids: string[],
    preciosVistos?: Record<string, number>
  ): Promise<ResultadoDeLote> => {
    const vistos = Object.fromEntries(
      Object.entries(preciosVistos ?? {}).filter(([, n]) => esPrecioVistoValido(n))
    );
    // Un lote de 200 escribe 200 precios en transacciones separadas: el timeout
    // por omisión no alcanza.
    const r = await conCambioPropio(
      apiClient.post<ResultadoDeLote>(
        '/api/price-proposals/approve-batch',
        Object.keys(vistos).length > 0 ? { ids, preciosVistos: vistos } : { ids },
        { timeout: 180000 }
      )
    );
    avisarPublicacion(r);
    return r;
  },

  /** Plan 2: la hoja de la vuelta de una estrategia vencida. */
  vuelta: async (strategyApplicationId: string): Promise<HojaDeVuelta> =>
    apiClient.get<HojaDeVuelta>(
      `/api/price-proposals/vencimientos/${encodeURIComponent(strategyApplicationId)}`
    ),

  /** Lote B1 (D2): el mismo motivo para todas. */
  /** Spec 275 (R3, D-R3-4): `preciosVistos`, como en `aprobarLote`. */
  descartarLote: async (
    ids: string[],
    motivo?: MotivoAlDescartar,
    preciosVistos?: Record<string, number>
  ): Promise<ResultadoDeLote> => {
    const vistos = Object.fromEntries(
      Object.entries(preciosVistos ?? {}).filter(([, n]) => esPrecioVistoValido(n))
    );
    return conCambioPropio(
      apiClient.post<ResultadoDeLote>(
        '/api/price-proposals/discard-batch',
        {
          ids,
          ...cuerpoDelMotivo(motivo),
          ...(Object.keys(vistos).length > 0 ? { preciosVistos: vistos } : {}),
        },
        { timeout: 180000 }
      )
    );
  },
};

export default priceProposalsApi;
